import React, {
  FunctionComponentElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppCoordinator } from './coordinators/AppCoordinator';
import loggedInUser, { acquireToken, setTokenRefresher, setLoginCallback, redirectToLogin } from './Auth0Authenticator';
import { isEmbeddedInIframe, notifyParentToLogin, requestAuthCacheFromParent } from './parentTokenRelay';
import { onLogoutBroadcast } from './AuthSessionSync';
import axios from 'axios';
import Utils from './Utils';
import { registerFetchInterceptor } from './fetchInterceptor';
import Bootstrapper from './Bootstrapper';
import { AppViewModel } from '../viewModels/AppViewModel';
import { AddEditMarketingEventViewModel } from '../viewModels/home/AddEditMarketingEventViewModel';
import { MarketingEventDetailViewModel } from '../viewModels/home/MarketingEventDetailViewModel';
import { MarketingEventsViewModel } from '../viewModels/home/MarketingEventsViewModel';
import { HomeViewModel } from '../viewModels/home/HomeViewModel';
import { DashboardViewModel } from '../viewModels/DashboardViewModel';
import { ErrorViewModel } from '../viewModels/ErrorViewModel';
import { RenewalHistoryViewModel } from '../viewModels/agents/policies/RenewalHistoryViewModel';
import { CreditCheckHistoryViewModel } from '../viewModels/agents/CreditCheckHistoryViewModel';
import { EmailViewModel } from '../viewModels/emails/EmailViewModel';
import { AutoDialerViewModel } from '../viewModels/autoDialers/AutoDialerViewModel';
import { ExistingCallListViewModel } from '../viewModels/autoDialers/ExistingCallListViewModel';
import { StartCallingViewModel } from '../viewModels/autoDialers/StartCallingViewModel';
import { AppView } from '../views/AppView';
import { AddEditMarketingEventView } from '../views/home/AddEditMarketingEventView';
import { MarketingEventDetailView } from '../views/home/MarketingEventDetailView';
import { MarketingEventsView } from '../views/home/MarketingEventsView';
import { HomeView } from '../views/home/HomeView';
import DashboardView from '../views/DashboardView';
import { ErrorView } from '../views/ErrorView';
import { RenewalHistoryView } from '../views/agents/policies/RenewalHistoryView';
import { CreditCheckHistoryView } from '../views/agents/CreditCheckHistoryView';
import { EmailView } from '../views/emails/EmailView';
import AutoDialerView from '../views/autoDialers/AutoDialerView';
import { ExistingCallListView } from '../views/autoDialers/ExistingCallListView';
import { StartCallingView } from '../views/autoDialers/StartCallingView';
import * as serviceWorker from '../serviceWorker';
import * as dateHelper from '../infrastructure/DateHelper';
import { useIframe } from '../hooks';
import { AppMenuItems } from './enum/Common';
import { IframeMessageType } from '../hooks/useIframe/useIframe.constants';
import PageContext from './PageContext';
import { listen, routes, history } from '../router';
import { Constants } from './enum/Constants';

function App() {
  const {
    user: auth0User,
    isAuthenticated,
    error,
    logout,
    loginWithRedirect,
    getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsloading] = useState(true);
  const [display, setDisplay] = useState<FunctionComponentElement<any> | null>(
    null,
  );
  const { navigateTo, messageToIframe } = useIframe();
  const [page, setPage] = useState<AppMenuItems | undefined>();
  const [path, setPath] = useState<string>();
  const [currentRoute, setCurrentRoute] = useState<string>();
  const isPopStateRef = useRef<boolean>(false);

  const bindBootstrapper = () => {
    Bootstrapper.bind(AppViewModel, AppView);
    Bootstrapper.bind(
      AddEditMarketingEventViewModel,
      AddEditMarketingEventView,
    );
    Bootstrapper.bind(MarketingEventDetailViewModel, MarketingEventDetailView);
    Bootstrapper.bind(MarketingEventsViewModel, MarketingEventsView);
    Bootstrapper.bind(HomeViewModel, HomeView);
    Bootstrapper.bind(DashboardViewModel, DashboardView);
    Bootstrapper.bind(ErrorViewModel, ErrorView);
    Bootstrapper.bind(RenewalHistoryViewModel, RenewalHistoryView);
    Bootstrapper.bind(CreditCheckHistoryViewModel, CreditCheckHistoryView);
    Bootstrapper.bind(EmailViewModel, EmailView);
    Bootstrapper.bind(AutoDialerViewModel, AutoDialerView);
    Bootstrapper.bind(ExistingCallListViewModel, ExistingCallListView);
    Bootstrapper.bind(StartCallingViewModel, StartCallingView);
  };

  const createProviderUser = (accessToken: string) => {
    let authKey;
    if (auth0User) {
      localStorage.setItem('auth0UserItem', JSON.stringify(auth0User));
      localStorage.setItem('userLogin', 'userLogin');
      localStorage.removeItem(Constants.currentAgentName);
      localStorage.removeItem('isAgentSelected');
      loggedInUser.displayName = (auth0User && auth0User.name) || '';
      loggedInUser.initialsName = (auth0User && auth0User.nickname) || '';
      loggedInUser.userName = (auth0User && auth0User.email) || '';
      loggedInUser.adUserId = (auth0User && auth0User.sub) || '';
    }
    if (accessToken) {
      authKey = `Bearer ${accessToken}`;
    }
    const url = Utils.getServiceUrlByEnvironment();
    var data = {
      Email: loggedInUser.userName,
      providerName: loggedInUser.displayName,
      providerID: auth0User && auth0User.sub,
      codeInvite: localStorage.getItem('invite_code') || '',
    };
    let host = Utils.getHost();
    const headers = {
      'Content-Type': 'application/json',
      hostname: host,
      Authorization: authKey,
      code_invite: localStorage.getItem('invite_code') || '',
    };
    axios
      .post(url + '/api/identity/v1/users/createProviderUser', data, {
        headers: headers,
      })
      .then(() => {
        setIsloading(false);
      })
      .catch((error) => {
        if (error.response == undefined || error.response.data.title === '-1') {
          localStorage.clear();
          setIsloading(false);
          return logout({
            logoutParams: { returnTo: window.location.origin },
          });
        } else {
          setIsloading(false);
        }
      });
  };

  const refreshAccessToken = () => {
    getAccessTokenSilently({
      authorizationParams: {
        audience: Utils.getAuth0ClientIdByEnvironment('audience'),
        scope: 'openid profile email offline_access',
      },
    })
      .then((accessToken) => {
        localStorage.setItem('auth0offline_access', accessToken);
      })
      .catch(async (error) => {
        if (error.error === 'login_required') {
          // While embedded, IMAS never holds its own refresh token (see
          // IntegrityConnectIframe's relay), so a local login_required just
          // means its silent refresh ran out on a clock Layout doesn't
          // share — not that the session is actually dead. Ask Layout for a
          // fresh one before tearing this session down.
          if (isEmbeddedInIframe()) {
            const relayed = await requestAuthCacheFromParent();
            if (relayed) return;
          }
          localStorage.clear();
          setIsloading(false);
          messageToIframe({
            type: IframeMessageType.LOGOUT,
            message: history.getActiveInstance().location.pathname,
          });
          return logout({
            logoutParams: { returnTo: window.location.origin },
          });
        }
      });
  };

  useEffect(() => {
    return onLogoutBroadcast(() => redirectToLogin());
  }, []);

  useEffect(() => {
    bindBootstrapper();
    if (isAuthenticated) {
      setIsloading(true);
      // Routed through the same cross-tab Web Lock used by getToken() so this
      // mount-time refresh can't race another tab's refresh of the same
      // refresh token (see Auth0Authenticator.ts). The nested call below,
      // stored via setTokenRefresher, must stay unwrapped — it's already
      // invoked through the lock later via getToken().
      acquireToken(() =>
        getAccessTokenSilently({
          authorizationParams: {
            audience: Utils.getAuth0ClientIdByEnvironment('audience'),
            scope: 'openid profile email offline_access',
          },
        })
      )
      .then((accessToken) => {
        setTokenRefresher(() =>
          getAccessTokenSilently({
            authorizationParams: {
              audience: Utils.getAuth0ClientIdByEnvironment('audience'),
              scope: 'openid profile email offline_access',
            },
          })
        );
        setLoginCallback((options) => loginWithRedirect(options));
        createProviderUser(accessToken);
        registerFetchInterceptor();
        serviceWorker.unregister();
        dateHelper.initialize();
      })
      .catch(() => {
        localStorage.clear();
        setIsloading(false);
        logout({ logoutParams: { returnTo: window.location.origin } });
      })
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (error) {
      messageToIframe({
        type: IframeMessageType.LOGOUT,
        message: history.getActiveInstance().location.pathname,
      });
    }
  }, [error]);

  const listenToParentWindowMessages = () => {
    const handleReceivedMessage = (event: MessageEvent) => {
      // Only Layout, our direct parent frame, may drive navigation or
      // impersonation state — without this, any page on the broad
      // CSP-allowed domains could post messages here.
      if (event.source !== window.parent) return;
      if (event.data.type === 'IMPERSONATION_CHANGE') {
        if (event.data.payload) {
          PageContext.setImpersonatingUserId(event.data.payload.userId);
          window.dispatchEvent(
            new CustomEvent('imas:impersonation-change', {
              detail: { userId: event.data.payload.userId },
            })
          );
        } else {
          PageContext.setImpersonatingUserId(null);
          window.dispatchEvent(
            new CustomEvent('imas:impersonation-change', {
              detail: { userId: null },
            })
          );
        }
        return;
      }
      if (event.data.type === 'page') {
        setPath(undefined);
        if (page === event.data.payload) setPage(undefined);
        if (event.data.payload != null) setPage(event.data.payload);
      }
      if (event.data.type === 'path') {
        if (path === event.data.payload) {
          setPath(undefined);
          setTimeout(() => {
            setPath(event.data.payload);
          }, 0);
        } else {
          setPath(event.data.payload);
        }
      }
    };
    window.addEventListener('message', handleReceivedMessage);
    return () => {
      window.removeEventListener('message', handleReceivedMessage);
    };
  };

  useEffect(() => {
    listenToParentWindowMessages();
  }, []);

  useEffect(() => {
    const unlisten = listen(({ name }) => {
      if (name && routes[name]) {
        setCurrentRoute(name);
      }
    });
    return unlisten;
  }, [messageToIframe, navigateTo]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.type === 'popstate') {
        isPopStateRef.current = true;
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      // This is needed to ensure the path is set before sending the message
      if (isPopStateRef.current) {
        isPopStateRef.current = false;
        messageToIframe({
          type: IframeMessageType.LOCATION_POPSTATE,
          message: history.getActiveInstance().location.pathname,
        });
        return; // Avoid sending location change message To Iframe for popstate events
      }
      messageToIframe({
        type: IframeMessageType.LOCATION_CHANGE,
        message: history.getActiveInstance().location.pathname,
      });
    }, 0);
  }, [currentRoute]);

  useEffect(() => {
    if (display) {
      refreshAccessToken();
      navigateTo(page);
    }
  }, [display, page]);

  useEffect(() => {
    if (display && path) {
      window.history.replaceState({}, '', path);
      refreshAccessToken();
      // This double execution of popstate is needed to load the page correctly when the path is set
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [display, path]);

  if (!isLoading && isAuthenticated && !display) {
    const app = new AppCoordinator();
    app.start().then(() => {
      setDisplay(app.display());
      messageToIframe({
        type: IframeMessageType.IC_APP_LOADED,
        message: IframeMessageType.IC_APP_LOADED,
      });
    });
  }

  if (display) return display;

  return <div className='Splash-Screen'></div>;
}

/**
 * Replaces auth0-react's withAuthenticationRequired: that HOC watches
 * isAuthenticated itself and calls loginWithRedirect() directly the instant
 * it goes false, with no way to intercept that call. isAuthenticated flips
 * false as a side effect of ANY failed getAccessTokenSilently() call (its
 * auth0-react wrapper re-derives isAuthenticated from client.getUser() in a
 * finally block) - including the routine refreshAccessToken() call this app
 * fires on every navigation. While embedded, that redirect always fails
 * (Auth0 refuses to be framed), leaving the iframe on a blank
 * "refused to connect" page instead of the app. This wrapper intercepts the
 * same isAuthenticated-false transition and asks Layout for a fresh session
 * first, only falling back to a real redirect (via Layout, never locally)
 * once that has nothing to offer either.
 */
function RequireAuth({ children }: { children: JSX.Element }) {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();
  const hasAuthenticatedOnceRef = useRef(false);
  const recoveringRef = useRef(false);

  if (isAuthenticated) {
    hasAuthenticatedOnceRef.current = true;
  }

  useEffect(() => {
    if (isLoading || isAuthenticated || recoveringRef.current) return;

    if (isEmbeddedInIframe()) {
      recoveringRef.current = true;
      requestAuthCacheFromParent()
        .then((token) => {
          if (!token) {
            notifyParentToLogin();
            return undefined;
          }
          // Re-derive isAuthenticated/user from the freshly seeded cache
          // rather than just having a bare token string on hand. Let a
          // failure here fall through to the outer .catch() below so it
          // still triggers notifyParentToLogin() instead of leaving the
          // splash screen up forever.
          return getAccessTokenSilently();
        })
        .catch(() => {
          notifyParentToLogin();
        })
        .finally(() => {
          recoveringRef.current = false;
        });
      return;
    }

    // withAuthenticationRequired always set this before redirecting; without
    // it, onRedirectCallback (Auth0ProviderWithNavigate.tsx) falls back to
    // window.location.href - the FULL absolute URL - and the app's custom
    // History router mishandles that as a relative path, concatenating it
    // onto the current origin instead of replacing it.
    loginWithRedirect({
      appState: {
        returnTo: window.location.pathname + window.location.search,
      },
    });
  }, [isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently]);

  // Block first paint until the very first sign-in resolves - App assumes a
  // real, authenticated user on mount. Once that has happened once, keep App
  // mounted through any later transient drop (a recovery attempt is already
  // running above) instead of tearing down its in-memory routing/display
  // state for what should be an invisible, momentary blip.
  if (isLoading || (!isAuthenticated && !hasAuthenticatedOnceRef.current)) {
    return <div className='Splash-Screen'></div>;
  }

  return children;
}

export default function AppWithAuthGuard() {
  return (
    <RequireAuth>
      <App />
    </RequireAuth>
  );
}
